import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authApi, authorizationApi, organizationApi } from '@/lib/pillarApi';
import { buildRoleScopedQueryKey } from '@/lib/reactQueryRoleScope';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import { runInBackground } from '@/lib/utils';
import { useAuthStore } from '@/store/auth';
import {
    type ActiveRoleAssignmentResponse,
    type GetOrganizationResponse,
    GetOrganizationResponseSchema,
    hasInternalAdminPrivileges,
    type ListOrganizationDocumentTemplatesResponse,
    type ValidateOrganizationDocumentTemplateResponse,
} from '@pillar-portal/api-types';
import {
    buildDocumentTemplateEditorState,
    buildDocumentTemplateSelectorKey,
    type DocumentTemplateEditorState,
    formatDocumentTemplateSelectorSummary,
} from '../OrganizationDetailContent';

function OrganizationDocumentTemplatesPageContent() {
    const log = createComponentLogger('OrganizationDocumentTemplatesPage');
    const navigate = useNavigate();
    const params = useParams();
    const organizationUuidParseResult = z.string().uuid().safeParse(params.id);
    if (!organizationUuidParseResult.success) {
        throw new Error(`Invalid organization UUID: ${params.id}`);
    }
    const organizationUuidFromRoute = organizationUuidParseResult.data;
    const queryClient = useQueryClient();
    const user = useAuthStore((state) => state.user);
    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const [documentTemplateEditorState, setDocumentTemplateEditorState] =
        useState<DocumentTemplateEditorState | null>(null);
    const [documentTemplateCardError, setDocumentTemplateCardError] =
        useState<string | null>(null);
    const [documentTemplateEditorError, setDocumentTemplateEditorError] =
        useState<string | null>(null);
    const [documentTemplateValidationResult, setDocumentTemplateValidationResult] =
        useState<ValidateOrganizationDocumentTemplateResponse | null>(null);

    const { data: activeRoleAssignment } = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            return await authApi.getActiveRoleAssignment();
        },
        enabled: !!user,
        staleTime: 30000,
    });

    const canManageOrganizations: boolean = hasInternalAdminPrivileges(
        activeRoleAssignment ?? null
    );

    const {
        data: org,
        isLoading,
        error,
    } = useQuery<GetOrganizationResponse>({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'organization',
            organizationUuidFromRoute,
        ]),
        queryFn: async () => {
            const response = await organizationApi.getOrganization(organizationUuidFromRoute);
            return GetOrganizationResponseSchema.parse(response);
        },
    });

    const { data: canEditConfigData } = useQuery({
        queryKey: buildRoleScopedQueryKey(activeRoleAssignmentId, [
            'authorization',
            'has_access_to_organization',
            organizationUuidFromRoute,
        ]),
        queryFn: async () => {
            const response =
                await authorizationApi.hasAccessToOrganization(organizationUuidFromRoute);
            return response.has_access;
        },
        enabled: org !== undefined,
    });

    const canEditConfig: boolean = canManageOrganizations && (canEditConfigData ?? false);
    const documentTemplatesQueryKey = buildRoleScopedQueryKey(activeRoleAssignmentId, [
        'organization',
        organizationUuidFromRoute,
        'document_templates',
    ]);

    const documentTemplatesQuery = useQuery<ListOrganizationDocumentTemplatesResponse>({
        queryKey: documentTemplatesQueryKey,
        queryFn: async () => {
            return await organizationApi.listDocumentTemplates(organizationUuidFromRoute);
        },
        enabled: org !== undefined && org.is_merchant && canEditConfig,
    });

    const validateDocumentTemplateMutation = useMutation({
        mutationFn: async (editorState: DocumentTemplateEditorState) => {
            return await organizationApi.validateDocumentTemplate(
                organizationUuidFromRoute,
                {
                    selector: editorState.selector,
                    template_source: editorState.templateSource,
                }
            );
        },
        onSuccess: (response) => {
            setDocumentTemplateEditorError(null);
            setDocumentTemplateValidationResult(response);
        },
        onError: (mutationError) => {
            setDocumentTemplateValidationResult(null);
            setDocumentTemplateEditorError(
                mutationError instanceof Error
                    ? mutationError.message
                    : 'Failed to validate document template'
            );
        },
    });

    const saveDocumentTemplateMutation = useMutation({
        mutationFn: async (editorState: DocumentTemplateEditorState) => {
            const requestBody = {
                selector: editorState.selector,
                name: editorState.name.trim(),
                description:
                    editorState.description.trim().length === 0
                        ? undefined
                        : editorState.description.trim(),
                template_source: editorState.templateSource,
                is_active: editorState.isActive,
            };
            if (editorState.overrideUuid === null) {
                return await organizationApi.createDocumentTemplate(
                    organizationUuidFromRoute,
                    requestBody
                );
            }
            return await organizationApi.updateDocumentTemplate(
                organizationUuidFromRoute,
                editorState.overrideUuid,
                requestBody
            );
        },
        onSuccess: () => {
            setDocumentTemplateCardError(null);
            setDocumentTemplateEditorError(null);
            setDocumentTemplateValidationResult(null);
            setDocumentTemplateEditorState(null);
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: documentTemplatesQueryKey,
                }),
                (invalidateError) => {
                    log.error(
                        'Error invalidating document template query:',
                        invalidateError
                    );
                }
            );
        },
        onError: (mutationError) => {
            setDocumentTemplateEditorError(
                mutationError instanceof Error
                    ? mutationError.message
                    : 'Failed to save document template'
            );
        },
    });

    const setDocumentTemplateActiveMutation = useMutation({
        mutationFn: async (params: {
            readonly documentTemplateUuid: string;
            readonly isActive: boolean;
        }) => {
            return await organizationApi.setDocumentTemplateActive(
                organizationUuidFromRoute,
                params.documentTemplateUuid,
                {
                    is_active: params.isActive,
                }
            );
        },
        onSuccess: () => {
            setDocumentTemplateCardError(null);
            runInBackground(
                queryClient.invalidateQueries({
                    queryKey: documentTemplatesQueryKey,
                }),
                (invalidateError) => {
                    log.error(
                        'Error invalidating document template query:',
                        invalidateError
                    );
                }
            );
        },
        onError: (mutationError) => {
            setDocumentTemplateCardError(
                mutationError instanceof Error
                    ? mutationError.message
                    : 'Failed to update document template state'
            );
        },
    });

    const documentTemplateOverrideBySelectorKey = useMemo(() => {
        const overrides = documentTemplatesQuery.data?.overrides ?? [];
        return new Map(
            overrides.map((override) => [
                buildDocumentTemplateSelectorKey(override.selector),
                override,
            ])
        );
    }, [documentTemplatesQuery.data?.overrides]);

    const registryEntriesWithOverrides = useMemo(() => {
        const registryEntries = documentTemplatesQuery.data?.registry_entries ?? [];
        return registryEntries.map((registryEntry) => {
            const selectorKey = buildDocumentTemplateSelectorKey(registryEntry.selector);
            return {
                registryEntry,
                override: documentTemplateOverrideBySelectorKey.get(selectorKey) ?? null,
            };
        });
    }, [documentTemplateOverrideBySelectorKey, documentTemplatesQuery.data?.registry_entries]);

    const isDocumentTemplateSaveEnabled =
        documentTemplateEditorState !== null &&
        documentTemplateEditorState.name.trim().length > 0 &&
        documentTemplateEditorState.templateSource.trim().length > 0;

    const closeDocumentTemplateEditor = (): void => {
        setDocumentTemplateEditorState(null);
        setDocumentTemplateEditorError(null);
        setDocumentTemplateValidationResult(null);
    };

    if (isLoading) {
        return <div>Loading...</div>;
    }
    if (error !== null) {
        return (
            <Alert variant="destructive">
                <AlertDescription>
                    {error instanceof Error ? error.message : 'Failed to load organization'}
                </AlertDescription>
            </Alert>
        );
    }
    if (org === undefined) {
        return (
            <Alert variant="destructive">
                <AlertDescription>Organization not found.</AlertDescription>
            </Alert>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold">Document Templates</h1>
                    <p className="text-sm text-muted-foreground">{org.name}</p>
                </div>
                <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                        runInBackground(
                            navigate(`/organizations/${organizationUuidFromRoute}?view=merchant`),
                            (navigationError) => {
                                log.error(
                                    'Error navigating to organization detail:',
                                    navigationError
                                );
                            }
                        );
                    }}
                >
                    Back to Organization
                </Button>
            </div>

            {!org.is_merchant ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        Document templates are only available for merchant organizations.
                    </AlertDescription>
                </Alert>
            ) : !canEditConfig ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        Access denied. Insufficient permissions.
                    </AlertDescription>
                </Alert>
            ) : (
                <Card id="document-templates-page-card">
                    <CardHeader>
                        <CardTitle>Document Templates</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {documentTemplateCardError !== null ? (
                            <Alert variant="destructive">
                                <AlertDescription>{documentTemplateCardError}</AlertDescription>
                            </Alert>
                        ) : null}
                        {documentTemplatesQuery.isLoading ? (
                            <p className="text-sm text-muted-foreground">
                                Loading document templates...
                            </p>
                        ) : null}
                        {documentTemplatesQuery.error !== null ? (
                            <Alert variant="destructive">
                                <AlertDescription>
                                    {documentTemplatesQuery.error instanceof Error
                                        ? documentTemplatesQuery.error.message
                                        : 'Failed to load document templates'}
                                </AlertDescription>
                            </Alert>
                        ) : null}
                        {!documentTemplatesQuery.isLoading &&
                            documentTemplatesQuery.error === null &&
                            registryEntriesWithOverrides.length === 0 ? (
                                <p className="text-sm text-muted-foreground">
                                    No registry-backed document templates are available.
                                </p>
                            ) : null}
                        {!documentTemplatesQuery.isLoading &&
                            documentTemplatesQuery.error === null &&
                            registryEntriesWithOverrides.map(({ registryEntry, override }) => (
                                <div
                                    key={buildDocumentTemplateSelectorKey(registryEntry.selector)}
                                    className="rounded-md border p-4 space-y-3"
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="space-y-1">
                                            <div className="font-medium">{registryEntry.name}</div>
                                            {registryEntry.description !== undefined ? (
                                                <div className="text-sm text-muted-foreground">
                                                    {registryEntry.description}
                                                </div>
                                            ) : null}
                                            <div className="text-xs text-muted-foreground">
                                                {formatDocumentTemplateSelectorSummary(
                                                    registryEntry.selector
                                                )}
                                            </div>
                                        </div>
                                        <span className="inline-flex items-center rounded-md border px-2 py-1 text-xs font-medium text-muted-foreground">
                                            {override === null
                                                ? 'Default only'
                                                : override.is_active
                                                  ? 'Override active'
                                                  : 'Override inactive'}
                                        </span>
                                    </div>
                                    <div className="grid gap-3 md:grid-cols-2">
                                        <div>
                                            <div className="text-xs text-muted-foreground">
                                                Allowed root variables
                                            </div>
                                            <div className="text-sm">
                                                {registryEntry.allowed_root_variables.join(', ')}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-muted-foreground">
                                                Current template name
                                            </div>
                                            <div className="text-sm">
                                                {override?.name ?? registryEntry.name}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-muted-foreground">
                                                Current description
                                            </div>
                                            <div className="text-sm">
                                                {override?.description ??
                                                    registryEntry.description ??
                                                    'Not configured'}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-xs text-muted-foreground">
                                                Source
                                            </div>
                                            <div className="text-sm">
                                                {override === null
                                                    ? 'Using built-in default template source'
                                                    : 'Using merchant override'}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            type="button"
                                            size="sm"
                                            onClick={() => {
                                                setDocumentTemplateEditorState(
                                                    buildDocumentTemplateEditorState(
                                                        registryEntry,
                                                        override
                                                    )
                                                );
                                                setDocumentTemplateEditorError(null);
                                                setDocumentTemplateValidationResult(null);
                                            }}
                                        >
                                            {override === null ? 'Create Override' : 'Edit Override'}
                                        </Button>
                                        {override !== null ? (
                                            <Button
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                disabled={
                                                    setDocumentTemplateActiveMutation.isPending
                                                }
                                                onClick={() => {
                                                    setDocumentTemplateActiveMutation.mutate({
                                                        documentTemplateUuid:
                                                            override.document_template_uuid,
                                                        isActive: !override.is_active,
                                                    });
                                                }}
                                            >
                                                {override.is_active ? 'Deactivate' : 'Activate'}
                                            </Button>
                                        ) : null}
                                    </div>
                                </div>
                            ))}
                    </CardContent>
                </Card>
            )}

            <Dialog
                open={documentTemplateEditorState !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        closeDocumentTemplateEditor();
                    }
                }}
            >
                <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Document Template Override</DialogTitle>
                        <DialogDescription>
                            Create or edit a merchant override for the selected
                            registry-backed template.
                        </DialogDescription>
                    </DialogHeader>
                    {documentTemplateEditorState !== null ? (
                        <div className="space-y-4">
                            <div className="text-sm text-muted-foreground">
                                {formatDocumentTemplateSelectorSummary(
                                    documentTemplateEditorState.selector
                                )}
                            </div>
                            <div className="text-sm text-muted-foreground">
                                Allowed root variables:{' '}
                                {documentTemplateEditorState.allowedRootVariables.join(', ')}
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="document_template_name">Template name</Label>
                                <Input
                                    id="document_template_name"
                                    value={documentTemplateEditorState.name}
                                    onChange={(event) => {
                                        setDocumentTemplateEditorState({
                                            ...documentTemplateEditorState,
                                            name: event.target.value,
                                        });
                                        setDocumentTemplateValidationResult(null);
                                    }}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="document_template_description">
                                    Description
                                </Label>
                                <Input
                                    id="document_template_description"
                                    value={documentTemplateEditorState.description}
                                    onChange={(event) => {
                                        setDocumentTemplateEditorState({
                                            ...documentTemplateEditorState,
                                            description: event.target.value,
                                        });
                                    }}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="document_template_source">
                                    Template source
                                </Label>
                                <textarea
                                    id="document_template_source"
                                    className="w-full min-h-[24rem] rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={documentTemplateEditorState.templateSource}
                                    onChange={(event) => {
                                        setDocumentTemplateEditorState({
                                            ...documentTemplateEditorState,
                                            templateSource: event.target.value,
                                        });
                                        setDocumentTemplateValidationResult(null);
                                    }}
                                />
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={documentTemplateEditorState.isActive}
                                    onChange={(event) => {
                                        setDocumentTemplateEditorState({
                                            ...documentTemplateEditorState,
                                            isActive: event.target.checked,
                                        });
                                    }}
                                />
                                Set override active
                            </label>
                            {documentTemplateEditorError !== null ? (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        {documentTemplateEditorError}
                                    </AlertDescription>
                                </Alert>
                            ) : null}
                            {documentTemplateValidationResult !== null ? (
                                documentTemplateValidationResult.valid ? (
                                    <Alert>
                                        <AlertDescription>
                                            Template is valid. Referenced root variables:{' '}
                                            {documentTemplateValidationResult.referenced_root_variables
                                                .join(', ') || 'None'}
                                        </AlertDescription>
                                    </Alert>
                                ) : (
                                    <Alert variant="destructive">
                                        <AlertDescription>
                                            {documentTemplateValidationResult.issues
                                                .map((issue) => issue.message)
                                                .join(' ')}
                                        </AlertDescription>
                                    </Alert>
                                )
                            ) : null}
                            <DialogFooter className="gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        setDocumentTemplateEditorState({
                                            ...documentTemplateEditorState,
                                            templateSource:
                                                documentTemplateEditorState.defaultTemplateSource,
                                        });
                                        setDocumentTemplateValidationResult(null);
                                    }}
                                >
                                    Reset to Default
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    disabled={validateDocumentTemplateMutation.isPending}
                                    onClick={() => {
                                        validateDocumentTemplateMutation.mutate(
                                            documentTemplateEditorState
                                        );
                                    }}
                                >
                                    {validateDocumentTemplateMutation.isPending
                                        ? 'Validating...'
                                        : 'Validate'}
                                </Button>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                        closeDocumentTemplateEditor();
                                    }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="button"
                                    disabled={
                                        saveDocumentTemplateMutation.isPending ||
                                        !isDocumentTemplateSaveEnabled
                                    }
                                    onClick={() => {
                                        saveDocumentTemplateMutation.mutate(
                                            documentTemplateEditorState
                                        );
                                    }}
                                >
                                    {saveDocumentTemplateMutation.isPending
                                        ? 'Saving...'
                                        : 'Save'}
                                </Button>
                            </DialogFooter>
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default function OrganizationDocumentTemplatesPage() {
    return <OrganizationDocumentTemplatesPageContent />;
}

export { OrganizationDocumentTemplatesPageContent };
